import { Link } from "react-router-dom";
import { ArrowUpRight, Maximize2, MapPin, Play, Calendar } from "lucide-react";

const imgUrl = (img) => {
  if (!img) return "";
  const u = typeof img === "string" ? img : img.url;
  if (!u) return "";
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
};

const STATUS_META = {
  disponible: { label: "Disponible", cls: "bg-[#FFC107] border-slate-900 text-slate-900" },
  occupe: { label: "Occupé", cls: "bg-orange-500 border-slate-900 text-white" },
  loue: { label: "Loué", cls: "bg-slate-700 border-slate-900 text-white" },
};

export const StatusBadge = ({ status, className = "" }) => {
  const meta = STATUS_META[status] || STATUS_META.disponible;
  return (
    <span className={`inline-block px-3 py-1 text-xs font-bold uppercase tracking-wider border ${meta.cls} ${className}`} data-testid={`status-${status}`}>
      {meta.label}
    </span>
  );
};

export const availabilityLabel = (available_from) => {
  if (!available_from) return { text: "Disponible immédiatement", future: false };
  try {
    const d = new Date(available_from);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (d <= today) return { text: "Disponible immédiatement", future: false };
    return {
      text: `Disponible dès le ${d.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}`,
      future: true,
    };
  } catch { return { text: "Disponible", future: false }; }
};

export const AvailabilityBadge = ({ available_from, className = "" }) => {
  const a = availabilityLabel(available_from);
  return (
    <span data-testid="availability-badge" className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold uppercase tracking-wider border ${a.future ? "bg-white border-slate-900 text-slate-900" : "bg-emerald-500 border-slate-900 text-white"} ${className}`}>
      <Calendar className="w-3 h-3" /> {a.text}
    </span>
  );
};

export const PropertyCard = ({ property }) => {
  const cover = property.images?.[0];
  const status = property.status || (property.available ? "disponible" : "loue");
  const hasVideo = property.videos?.length > 0;
  return (
    <Link to={`/locaux/${property.slug || property.id}`} data-testid={`property-card-${property.id}`}
      className="group block bg-white border border-slate-900/15 card-hover">
      <div className="aspect-[4/3] overflow-hidden bg-slate-100 relative">
        {cover ? (
          <img src={imgUrl(cover)} alt={property.title} loading="lazy" decoding="async"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full grid-bg" />
        )}
        <div className="absolute top-4 left-4 flex flex-col gap-2 items-start">
          <StatusBadge status={status} />
          {status === "disponible" && <AvailabilityBadge available_from={property.available_from} />}
        </div>
        {hasVideo && (
          <div className="absolute top-4 right-4 w-9 h-9 bg-white border border-slate-900 flex items-center justify-center">
            <Play className="w-4 h-4" strokeWidth={2.5} fill="currentColor" />
          </div>
        )}
      </div>
      <div className="p-6">
        <div className="flex items-start justify-between gap-4 mb-3">
          <h3 className="font-display font-black text-xl leading-tight">{property.title}</h3>
          <ArrowUpRight className="w-5 h-5 shrink-0 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          <span className="tag flex items-center gap-1.5"><Maximize2 className="w-3 h-3"/>{property.surface_min}–{property.surface_max} m²</span>
          <span className="tag flex items-center gap-1.5"><MapPin className="w-3 h-3"/>{property.location}</span>
        </div>
        <div className="flex items-end justify-between pt-4 border-t border-slate-900/10">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">À partir de</div>
            <div className="font-display font-black text-2xl">{property.price_monthly.toLocaleString("fr-FR")} €<span className="text-sm font-normal text-slate-500">/mois HT</span></div>
          </div>
          <span
            className="text-xs font-bold uppercase tracking-wider bg-slate-900 text-white px-3 py-2 group-hover:bg-[#FFC107] group-hover:text-slate-900 transition-colors"
            data-testid={`property-cta-visit-${property.id}`}
          >
            Demander une visite →
          </span>
        </div>
      </div>
    </Link>
  );
};

export { imgUrl };
